import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { fontMerriweather, fontRoboto, fontRobotoCondensed } from "@product/scmp-sdk";

import { ArticleShareWidget } from "~/components/article/article-share-widget";
import { BaseImage } from "~/components/common/base-image";
import { EntityLink } from "~/components/entity-link";
import { setFullViewportWidthStyles } from "~/lib/styles";

export const BackgroundImageContainer = styled.div`
  z-index: -1;

  block-size: 100vh;

  ${props => props.theme.breakpoints.up("tablet")} {
    block-size: 100%;
  }
`;

export const StyledBaseImage = styled(BaseImage)`
  object-position: 50% 50%;
`;

export const Content = styled.div`
  ${setFullViewportWidthStyles()}
  position: absolute;
  inset-block-start: 0;
  z-index: 1;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  block-size: 100%;

  background: rgba(0, 0, 0, 0.4);
`;

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  inline-size: 300px;

  ${props => props.theme.breakpoints.up("tablet")} {
    inline-size: 502px;
  }
`;

export const Headline = styled.div`
  color: #ffffff;
  font-weight: 300;
  font-size: 32px;
  font-family: ${fontRoboto};
  line-height: 38px;
  text-align: center;

  ::before {
    content: " ";

    display: block;

    inline-size: 44px;
    block-size: 6px;
    margin-block: 0 8px;
    margin-inline: auto;

    background-color: #ffca05;

    ${props => props.theme.breakpoints.up("tablet")} {
      margin-block: 0 28px;
    }
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    font-size: 40px;
    line-height: 52px;
  }
`;

export const StyledEntityLink = styled(EntityLink)`
  margin-block-start: 20px;
  padding-block: 4px;
  padding-inline: 8px;

  color: #000000;
  font-size: 18px;
  font-family: ${fontRobotoCondensed};
  line-height: 21px;

  background-color: #ffffff;
  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 40px;
  }
`;

export const StyledArticleShareWidget = styled(ArticleShareWidget)`
  display: none;
  ${props => props.theme.breakpoints.up("tablet")} {
    display: flex;

    margin-block-start: 24px;
  }
`;

export type ContainerProps = {
  $variant?: "photo-essay" | "long-read";
};

export const Container = styled.div<ContainerProps>`
  ${setFullViewportWidthStyles()}

  position: relative;

  block-size: 100%;

  overflow: hidden;

  ${props => props.theme.breakpoints.up("tablet")} {
    block-size: 750px;
  }

  ${props => {
    switch (props.$variant) {
      case "long-read":
        return css`
          ${Wrapper} {
            ${props.theme.breakpoints.only("mobile")} {
              margin-inline-start: 32px;
            }
          }

          ${Content}, ${Wrapper} {
            align-items: flex-start;
          }

          ${Headline} {
            max-inline-size: 218px;

            text-align: start;

            ::before {
              margin-inline: unset;
            }
          }

          ${props.theme.breakpoints.up("tablet")} {
            ${Content}, ${Wrapper} {
              align-items: center;
            }

            ${Headline} {
              max-inline-size: unset;

              text-align: center;

              ::before {
                margin-inline: auto;
              }
            }
          }
        `;
      case "photo-essay":
        return css`
          ${Headline} {
            font-weight: 900;
            font-size: 24px;
            font-family: ${fontMerriweather};
            line-height: 33px;

            ${props.theme.breakpoints.up("tablet")} {
              font-size: 40px;
              line-height: 52px;
            }
          }

          ${Content} {
            ${props.theme.breakpoints.only("mobile")} {
              justify-content: flex-start;
            }
          }

          ${Wrapper} {
            ${props.theme.breakpoints.only("mobile")} {
              margin-block-start: 146px;
            }
          }
        `;
    }
  }}
`;
